import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { AuthContext } from '../shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const isoDay = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const KIND_COLORS = {
  in: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  out: 'text-rose-700 bg-rose-50 border-rose-200',
};

const ArchiveManagement = () => {
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.role === 'super_admin';
  const canUnarchive = isSuperAdmin || user?.role === 'general_accountant';

  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState('');
  const [view, setView] = useState('available'); // 'available' | 'archived'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Archive / Unarchive dialog state
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  // Load agencies (super_admin sees all).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/agencies`, authHeaders());
        setAgencies(data || []);
        // Default to admin's own agency, or first one if none.
        if (!selectedAgency) {
          const own = user?.agency_id;
          setSelectedAgency(own || (data?.[0]?.id ?? ''));
        }
      } catch (e) {
        toast.error(`تعذر تحميل الوكالات: ${e.response?.data?.detail || e.message}`);
      }
    })();
  }, []);

  const fetchItems = useCallback(async () => {
    if (!selectedAgency) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ agency_id: selectedAgency });
      if (startDate) params.set('start_date', `${startDate}T00:00:00`);
      if (endDate) params.set('end_date', `${endDate}T23:59:59`);
      const endpoint = view === 'available'
        ? 'available-entries'
        : 'archived-entries';
      const { data } = await axios.get(
        `${API}/cash-transfers/v2/${endpoint}?${params}`,
        authHeaders(),
      );
      setItems(data.items || []);
    } catch (e) {
      toast.error(`تعذر التحميل: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedAgency, view, startDate, endDate]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((it) =>
      [it.client_name, it.employee_name, it.service_name, it.receipt_no, it.description, it.archive_reason]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [items, search]);

  const totals = useMemo(() => {
    const totalIn = filtered.filter((it) => it.kind === 'in').reduce((s, it) => s + Number(it.amount || 0), 0);
    const totalOut = filtered.filter((it) => it.kind === 'out').reduce((s, it) => s + Number(it.amount || 0), 0);
    return {
      in: totalIn, out: totalOut, net: totalIn - totalOut,
      count: filtered.length,
      countIn: filtered.filter((it) => it.kind === 'in').length,
      countOut: filtered.filter((it) => it.kind === 'out').length,
    };
  }, [filtered]);

  const openArchive = (it) => {
    if (!isSuperAdmin) {
      toast.error('الأرشفة محصورة على المدير العام فقط');
      return;
    }
    setTarget({ ...it, _action: 'archive' });
    setReason('');
  };
  const openUnarchive = (it) => {
    if (!canUnarchive) {
      toast.error('الاستعادة للمدير أو المحاسب فقط');
      return;
    }
    setTarget({ ...it, _action: 'unarchive' });
    setReason('');
  };

  const submitAction = async () => {
    if (!target) return;
    const r = reason.trim();
    if (r.length < 3) {
      toast.error('يرجى إدخال السبب (3 أحرف على الأقل)');
      return;
    }
    setBusy(true);
    try {
      const endpoint = target._action === 'archive' ? 'archive-entry' : 'unarchive-entry';
      const { data } = await axios.post(
        `${API}/cash-transfers/v2/${endpoint}`,
        {
          kind: target.kind,
          source_collection: target.source_collection,
          source_id: target.source_id,
          fe_id: target.fe_id || null,
          reason: r,
        },
        authHeaders(),
      );
      toast.success(data.message || 'تم');
      setTarget(null);
      setReason('');
      await fetchItems();
    } catch (e) {
      toast.error(`فشل: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!canUnarchive) {
    return (
      <div className="p-8 text-center text-red-700 bg-red-50 border rounded" dir="rtl">
        ❌ هذه الصفحة للمدير العام أو المحاسب العام فقط.
      </div>
    );
  }

  const selectedAgencyName = agencies.find((a) => a.id === selectedAgency)?.name || '—';

  return (
    <div className="p-4 md:p-6 space-y-4" dir="rtl" data-testid="archive-mgmt-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-amber-800 flex items-center gap-2">
            🗄️ مركز الأرشيف
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {isSuperAdmin
              ? 'استعرض جميع الوصولات عبر كل الوكالات. اختر الوكالة والوصل الذي تريد أرشفته لإخفائه عن الموظف في التحويل النقدي.'
              : 'استعرض الوصولات المؤرشفة واستعدها عند الحاجة.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={view === 'available' ? 'default' : 'outline'}
            onClick={() => setView('available')}
            className={view === 'available' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
            data-testid="arch-view-available"
          >
            📋 الوصولات المتاحة
          </Button>
          <Button
            variant={view === 'archived' ? 'default' : 'outline'}
            onClick={() => setView('archived')}
            className={view === 'archived' ? 'bg-gray-700 hover:bg-gray-800 text-white' : ''}
            data-testid="arch-view-archived"
          >
            🗃️ المؤرشفة
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div>
          <Label className="text-xs font-bold">الوكالة</Label>
          <Select value={selectedAgency} onValueChange={setSelectedAgency}>
            <SelectTrigger data-testid="arch-agency-select">
              <SelectValue placeholder="اختر الوكالة" />
            </SelectTrigger>
            <SelectContent>
              {agencies.map((a) => (
                <SelectItem key={a.id} value={a.id} data-testid={`arch-agency-opt-${a.id}`}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">من تاريخ</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid="arch-start-date"
          />
        </div>
        <div>
          <Label className="text-xs">إلى تاريخ</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            data-testid="arch-end-date"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">بحث</Label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="عميل / موظف / وصل / سبب…"
            data-testid="arch-search"
          />
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 rounded border bg-white">
          <div className="text-xs text-gray-600">الوكالة</div>
          <div className="text-sm font-bold truncate">{selectedAgencyName}</div>
        </div>
        <div className="p-3 rounded border bg-white">
          <div className="text-xs text-gray-600">عدد الوصولات ({view === 'available' ? 'متاحة' : 'مؤرشفة'})</div>
          <div className="text-lg font-bold" data-testid="arch-total-count">
            {totals.count} <span className="text-xs text-gray-500">(⬇{totals.countIn} · ⬆{totals.countOut})</span>
          </div>
        </div>
        <div className="p-3 rounded border bg-emerald-50">
          <div className="text-xs text-emerald-800">إجمالي الداخل</div>
          <div className="text-lg font-bold text-emerald-800">{fmt(totals.in)} <span className="text-xs">د.ج</span></div>
        </div>
        <div className="p-3 rounded border bg-rose-50">
          <div className="text-xs text-rose-800">إجمالي الخارج</div>
          <div className="text-lg font-bold text-rose-800">{fmt(totals.out)} <span className="text-xs">د.ج</span></div>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-auto max-h-[65vh]">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              <th className="px-2 py-2 text-right">التاريخ</th>
              <th className="px-2 py-2 text-right">رقم الوصل</th>
              <th className="px-2 py-2 text-right">العميل</th>
              <th className="px-2 py-2 text-right">الموظف</th>
              <th className="px-2 py-2 text-right">نوع الخدمة</th>
              <th className="px-2 py-2 text-center">الاتجاه</th>
              <th className="px-2 py-2 text-right">المبلغ</th>
              {view === 'archived' && (
                <>
                  <th className="px-2 py-2 text-right">سبب الأرشفة</th>
                  <th className="px-2 py-2 text-right">أُرشِفَ بواسطة</th>
                  <th className="px-2 py-2 text-right">تاريخ الأرشفة</th>
                </>
              )}
              <th className="px-2 py-2 text-center w-28">إجراء</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="11" className="text-center py-8 text-gray-500">⏳ جارٍ التحميل…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="11" className="text-center py-8 text-gray-500">
                {view === 'available' ? 'لا توجد وصولات متاحة' : 'لا توجد وصولات مؤرشفة'}
              </td></tr>
            )}
            {!loading && filtered.map((it) => (
              <tr
                key={`${it.kind}|${it.source_collection}|${it.source_id}`}
                className="border-t hover:bg-amber-50"
                data-testid={`arch-row-${it.source_id}`}
              >
                <td className="px-2 py-2 whitespace-nowrap">{isoDay(it.date)}</td>
                <td className="px-2 py-2 text-xs font-mono">{it.receipt_no || '—'}</td>
                <td className="px-2 py-2">{it.client_name || '—'}</td>
                <td className="px-2 py-2">{it.employee_name || '—'}</td>
                <td className="px-2 py-2">{it.service_name || '—'}</td>
                <td className="px-2 py-2 text-center">
                  <Badge variant="outline" className={KIND_COLORS[it.kind]}>
                    {it.kind === 'in' ? '⬇ داخل' : '⬆ خارج'}
                  </Badge>
                </td>
                <td className={`px-2 py-2 font-semibold text-right ${it.kind === 'in' ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {it.kind === 'in' ? '+' : '−'}{fmt(it.amount)}
                </td>
                {view === 'archived' && (
                  <>
                    <td className="px-2 py-2 text-xs max-w-[200px] truncate" title={it.archive_reason}>
                      {it.archive_reason || '—'}
                    </td>
                    <td className="px-2 py-2 text-xs">{it.archived_by_name || '—'}</td>
                    <td className="px-2 py-2 text-xs whitespace-nowrap">{isoDay(it.archived_at)}</td>
                  </>
                )}
                <td className="px-2 py-2 text-center">
                  {view === 'available' ? (
                    isSuperAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-amber-700 border-amber-300 hover:bg-amber-50 h-7 px-2"
                        onClick={() => openArchive(it)}
                        data-testid={`arch-archive-btn-${it.source_id}`}
                      >
                        🗄️ أرشفة
                      </Button>
                    )
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 h-7 px-2"
                      onClick={() => openUnarchive(it)}
                      data-testid={`arch-unarchive-btn-${it.source_id}`}
                    >
                      ↩️ استعادة
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Archive / Unarchive dialog */}
      <Dialog open={!!target} onOpenChange={(o) => { if (!o) { setTarget(null); setReason(''); } }}>
        <DialogContent className="max-w-lg" dir="rtl" data-testid="arch-action-dialog">
          <DialogHeader>
            <DialogTitle className={`text-lg font-bold ${target?._action === 'archive' ? 'text-amber-800' : 'text-emerald-800'}`}>
              {target?._action === 'archive'
                ? '🗄️ أرشفة وصل — يخرج من الرصيد وقائمة التحويل'
                : '↩️ استعادة وصل مؤرشف — يعود إلى الرصيد'}
            </DialogTitle>
          </DialogHeader>
          {target && (
            <div className="space-y-3">
              <div className="p-3 rounded border-2 border-amber-200 bg-amber-50 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-gray-600">التاريخ:</span> <strong>{isoDay(target.date)}</strong></div>
                  <div><span className="text-gray-600">المبلغ:</span> <strong className={target.kind === 'in' ? 'text-emerald-700' : 'text-rose-700'}>{target.kind === 'in' ? '+' : '−'}{fmt(target.amount)} د.ج</strong></div>
                  <div><span className="text-gray-600">العميل:</span> <strong>{target.client_name || '—'}</strong></div>
                  <div><span className="text-gray-600">النوع:</span> <strong>{target.service_name || '—'}</strong></div>
                  <div className="col-span-2"><span className="text-gray-600">رقم الوصل:</span> <strong className="font-mono">{target.receipt_no || '—'}</strong></div>
                </div>
              </div>
              {target._action === 'archive' && (
                <div className="p-2.5 rounded border border-red-200 bg-red-50 text-xs text-red-800">
                  ⚠️ بعد الأرشفة:
                  <ul className="list-disc pr-5 mt-1 space-y-0.5">
                    <li>لن يظهر في نافذة التحويل النقدي الجديد للموظف.</li>
                    <li>سيُستَبعد من رصيد الوكالة القابل للتحويل.</li>
                    <li>يمكن استعادته لاحقاً من هذه الصفحة (تبويب &quot;المؤرشفة&quot;).</li>
                  </ul>
                </div>
              )}
              <div>
                <Label className="text-xs font-bold">
                  السبب <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={target._action === 'archive'
                    ? 'مثال: مكرر مع وصل رقم … / خطأ إدخال / وصل ملغى'
                    : 'مثال: أُرشف بالخطأ / يعود للتحويل'}
                  data-testid="arch-action-reason"
                />
                <p className="text-[10px] text-gray-500 mt-0.5">
                  يُسجّل في السجل المالي للتدقيق. مطلوب — على الأقل 3 أحرف.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => { setTarget(null); setReason(''); }}
                  disabled={busy}
                  data-testid="arch-action-cancel"
                >
                  إلغاء
                </Button>
                <Button
                  onClick={submitAction}
                  disabled={busy || reason.trim().length < 3}
                  className={target._action === 'archive'
                    ? 'bg-amber-600 hover:bg-amber-700 text-white'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                  data-testid="arch-action-confirm"
                >
                  {busy ? '⏳ جارٍ…' : target._action === 'archive' ? '🗄️ تأكيد الأرشفة' : '↩️ تأكيد الاستعادة'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArchiveManagement;
